import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { useEffect, useRef } from "react";
import { useSelector } from "@tanstack/react-store";
import type { ColumnSizingState, RowData } from "@tanstack/react-table";
import type { DataGridHeader } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { createColumnResizer, type ResizeEdge } from "./columnResizer";
import styles from "./Header.module.scss";

const KEYBOARD_STEP = 8;

interface ResizeHandleProps<TData extends RowData> {
    header: DataGridHeader<TData>;
    controller: GridController;
    label: string;
    isTabbable: boolean;
    /** `end` normally, `start` for end-pinned columns, which grow inward. */
    edge: ResizeEdge;
}

/**
 * Column edge handle: drag (mouse or touch) to resize, double-click to fit
 * content, arrow keys for 8px steps when focused (`createColumnResizer`),
 * and Escape puts back the widths from when it took focus. Text selection is
 * off on the root while a resize runs.
 */
export const ResizeHandle = <TData extends RowData>({
    header,
    controller,
    label,
    isTabbable,
    edge,
}: Readonly<ResizeHandleProps<TData>>) => {
    const column = header.column;
    const table = column.table;
    const isResizing = useSelector(
        table.atoms.columnResizing,
        (resizing) => resizing.isResizingColumn === column.id,
    );
    const size = useSelector(table.atoms.columnSizing, () => column.getSize());
    const containerWidth = useSelector(controller.containerWidth);
    const sizingAtFocusRef = useRef<ColumnSizingState | null>(null);
    const { minSize = 0 } = column.columnDef;

    useEffect(() => {
        const root = controller.root;
        if (!isResizing || !root) {
            return;
        }
        root.setAttribute("data-dt-resizing", "");
        return () => root.removeAttribute("data-dt-resizing");
    }, [isResizing, controller]);

    const resizer = createColumnResizer({ column, controller, edge });

    const startResize = (event: ReactPointerEvent) => {
        if (event.button !== 0) {
            return;
        }
        event.stopPropagation();
        event.preventDefault();
        resizer.startDrag({ startX: event.clientX });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        const sizingAtFocus = sizingAtFocusRef.current;
        if (
            event.key === "Escape" &&
            sizingAtFocus &&
            sizingAtFocus !== table.atoms.columnSizing.get()
        ) {
            event.preventDefault();
            event.stopPropagation();
            table.setColumnSizing(sizingAtFocus);
            return;
        }
        const step =
            { ArrowLeft: -KEYBOARD_STEP, ArrowRight: KEYBOARD_STEP }[
                event.key
            ] ?? 0;
        if (step === 0) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        resizer.resizeBy(step);
    };

    return (
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${label}`}
            aria-valuenow={Math.round(size)}
            aria-valuetext={`${Math.round(size)} pixels`}
            aria-valuemin={minSize}
            aria-valuemax={Math.round(Math.max(size, containerWidth))}
            tabIndex={isTabbable ? 0 : -1}
            className={styles.resizeHandle}
            data-edge={edge}
            data-dt-no-drag=""
            data-resizing={isResizing || undefined}
            onPointerDown={startResize}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => {
                event.stopPropagation();
                resizer.fitToContent(controller.root);
            }}
            onFocus={() => {
                sizingAtFocusRef.current = table.atoms.columnSizing.get();
            }}
            onKeyDown={handleKeyDown}
        />
    );
};
